/**
 * Admin store module
 * Manages admin-related state and actions
 */
import { defineStore } from 'pinia'
import { adminApi } from '@/services/api'

export const useAdminStore = defineStore('admin', {
  state: () => ({
    dashboardData: null,
    betaUsers: [],
    featureFlags: [],
    loading: false,
    error: null
  }),

  getters: {
    openFeedbackCount: (state) => state.dashboardData?.open_feedback || 0,
    activeFeatureFlags: (state) => state.featureFlags.filter(flag => flag.is_active),
    pendingBetaUsers: (state) => state.betaUsers.filter(user => user.status === 'pending')
  },

  actions: {
    async fetchDashboardData() {
      this.loading = true
      this.error = null
      try {
        const response = await adminApi.getDashboard()
        this.dashboardData = response.data
      } catch (error) {
        this.error = error.message || 'Failed to load dashboard data'
        console.error('Dashboard fetch error:', error)
        throw error
      } finally {
        this.loading = false
      }
    },

    async fetchBetaUsers(filters = {}) {
      this.loading = true
      this.error = null
      try {
        const response = await adminApi.getBetaUsers(filters)
        this.betaUsers = response.data.users || []
      } catch (error) {
        this.error = error.message || 'Failed to load beta users'
        console.error('Beta users fetch error:', error)
      } finally {
        this.loading = false
      }
    },

    async approveBetaUser(userId) {
      try {
        await adminApi.approveBetaUser(userId)
        // Update local state
        const user = this.betaUsers.find(u => u.id === userId)
        if (user) {
          user.status = 'approved'
          user.approved_at = new Date().toISOString()
        }
      } catch (error) {
        this.error = error.message || 'Failed to approve user'
        throw error
      }
    },

    async rejectBetaUser(userId) {
      try {
        await adminApi.rejectBetaUser(userId)
        // Remove from local state
        this.betaUsers = this.betaUsers.filter(u => u.id !== userId)
      } catch (error) {
        this.error = error.message || 'Failed to reject user'
        throw error
      }
    },

    async fetchFeatureFlags() {
      this.loading = true
      this.error = null
      try {
        const response = await adminApi.getFeatureFlags()
        this.featureFlags = response.data.flags || []
      } catch (error) {
        this.error = error.message || 'Failed to load feature flags'
        console.error('Feature flags fetch error:', error)
      } finally {
        this.loading = false
      }
    },

    async createFeatureFlag(flagData) {
      try {
        const response = await adminApi.createFeatureFlag(flagData)
        this.featureFlags.push(response.data)
        return response.data
      } catch (error) {
        this.error = error.message || 'Failed to create feature flag'
        throw error
      }
    },

    async updateFeatureFlag(flagId, updates) {
      try {
        const response = await adminApi.updateFeatureFlag(flagId, updates)
        const index = this.featureFlags.findIndex(f => f.id === flagId)
        if (index !== -1) {
          this.featureFlags[index] = response.data
        }
        return response.data
      } catch (error) {
        this.error = error.message || 'Failed to update feature flag'
        throw error
      }
    },

    async checkFeatureFlag(flagName, userId) {
      try {
        const response = await adminApi.checkFeatureFlag(flagName, userId)
        return response.data
      } catch (error) {
        this.error = error.message || 'Failed to check feature flag'
        throw error
      }
    },

    async exportBetaUsers(format = 'csv') {
      try {
        const response = await adminApi.exportBetaUsers(format)
        // Trigger download
        const blob = new Blob([response.data], { type: 'text/csv' })
        const url = window.URL.createObjectURL(blob)
        const a = document.createElement('a')
        a.href = url
        a.download = `beta-users-${new Date().toISOString().split('T')[0]}.csv`
        a.click()
        window.URL.revokeObjectURL(url)
      } catch (error) {
        this.error = error.message || 'Failed to export users'
        throw error
      }
    }
  }
})